import { AdPlatform, CampaignObjective, CreativeType } from '@/shared/types/enums';

export type CampaignManagementType = 'SELF_MANAGED' | 'EVENTS_MANAGED';

export interface CampaignWizardFormData {
  // Event linkage — either from Events.com or manually entered
  eventId?: string;
  eventName?: string;
  eventVenue?: string;

  // Campaign management type
  managementType: CampaignManagementType;

  // Step 1: Basic details
  platform: AdPlatform;
  adAccountId?: string;
  /**
   * Facebook Page that publishes the ad. Required for Meta self-managed
   * campaigns — Meta won't create an Ad without `object_story_spec.page_id`.
   * Picked from `GET /ad-accounts/:id/meta-pages` after the ad account is
   * chosen; auto-selected when the user has a single Page.
   */
  pageId?: string;
  /**
   * Meta only. Set when the campaign promotes an already-published Page post
   * (`{page-id}_{post-id}`) instead of creative built in the wizard.
   */
  boostPostId?: string;
  pageName?: string;

  name: string;
  objective: CampaignObjective;
  /** Which budget mode the user chose — forces exclusive daily vs lifetime. */
  budgetStrategy: 'daily' | 'lifetime';
  dailyBudgetCents: number;
  totalBudgetCents: number;
  currency: string;
  startDate: string;
  endDate: string;

  /**
   * Conversion-objective fields (Meta only for now). Populated on Step 1
   * when the selected objective optimizes for conversions; ignored for
   * Awareness/Traffic/Engagement.
   */
  pixelId?: string;
  conversionEvent?: string;
  /** HTTPS landing URL the ads click to. Required for conversion objectives. */
  landingUrl?: string;

  // Step 2: Audience Targeting
  targetingConfig: TargetingConfig;

  // Step 3: Creative
  creatives: WizardCreative[];
  destinationUrl: string;

  // Step 4: Review
  agreedToTerms: boolean;
}

export interface TargetingConfig {
  locations: LocationTarget[];
  ageMin: number;
  ageMax: number;
  /**
   * Google-only age bracket for the Reach Forecast call — Meta uses
   * ageMin/ageMax instead. Must be one of {@link GOOGLE_AGE_RANGES}; that
   * list has to match the backend's `GOOGLE_AGE_RANGES` enum exactly, since
   * Google's Reach Planning API only accepts these fixed brackets.
   */
  googleAgeRange?: GoogleAgeRange;
  genders: Gender[];
  interests: InterestTarget[];
  keywords: string[];
  /** Meta behavior targeting — persisted alongside interests with type: 'BEHAVIOR'. */
  behaviors: InterestTarget[];
}

/**
 * Google Reach Planning API's fixed age brackets (`AgeRangeType`). Must
 * match `GOOGLE_AGE_RANGES` in the backend's
 * `google-api.constants.ts` exactly — Google validates against this literal
 * list, so a value outside it is rejected with a 400.
 */
export const GOOGLE_AGE_RANGES = [
  'AGE_RANGE_18_24',
  'AGE_RANGE_25_34',
  'AGE_RANGE_35_44',
  'AGE_RANGE_45_54',
  'AGE_RANGE_55_64',
  'AGE_RANGE_65_UP',
  'AGE_RANGE_18_65_UP',
] as const;

export type GoogleAgeRange = (typeof GOOGLE_AGE_RANGES)[number];

/** Human-readable labels for the Google age bracket picker. */
export const GOOGLE_AGE_RANGE_LABELS: Record<GoogleAgeRange, string> = {
  AGE_RANGE_18_65_UP: 'All ages',
  AGE_RANGE_18_24: '18–24',
  AGE_RANGE_25_34: '25–34',
  AGE_RANGE_35_44: '35–44',
  AGE_RANGE_45_54: '45–54',
  AGE_RANGE_55_64: '55–64',
  AGE_RANGE_65_UP: '65+',
};

export interface LocationTarget {
  id: string;
  name: string;
  type: 'country' | 'city' | 'region';
  radius?: number;
  radiusUnit?: 'km' | 'mi';
  /**
   * Google's approximate targetable population, present only on rows from the
   * Google geo target search. Sent back with the estimate request so the
   * backend can size the audience from a real figure.
   */
  reach?: number;
}

export type Gender = 'male' | 'female' | 'all';

export interface InterestTarget {
  id: string;
  name: string;
  category?: string;
  /**
   * For Google, distinguishes between a UserInterest constant (affinity /
   * in-market) and a custom Audience resource; the backend routes them to
   * different criterion types when launching. 'BEHAVIOR' marks Meta behavior
   * targeting entries. Undefined for plain Meta interests.
   */
  type?: 'USER_INTEREST' | 'AUDIENCE' | 'BEHAVIOR';
}

export interface GoogleKeywordSuggestion {
  text: string;
  avgMonthlySearches: number;
  competition: 'UNKNOWN' | 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface GoogleAudienceSuggestion {
  id: string;
  displayName: string;
  type: 'USER_INTEREST' | 'AUDIENCE';
}

export interface WizardCreative {
  type: CreativeType;
  headlines: string[];
  descriptions: string[];
  callToAction: CallToAction;
  /**
   * Landscape (1.91:1) image pool. Meta/Google mix every entry freely with
   * the headline/description pools (up to 10 on Meta, 15 on Google); X and
   * TikTok use a single image.
   */
  mediaFiles: File[];
  mediaPreviews: string[];
  generatedImages?: GeneratedCreativeImage[];
  /** Square (1:1) image pool — only used for Google Display campaigns. */
  squareMediaFiles?: File[];
  squareMediaPreviews?: string[];
  squareGeneratedImages?: GeneratedCreativeImage[];
  /** Video slot — only used for VIDEO creatives. */
  videoFile?: File;
  /** Local blob URL for the picked video, used for preview before upload. */
  videoPreview?: string;
  /** S3 storage key returned by the presigned upload, sent on launch. */
  videoStorageKey?: string;
  /** Cover image for a VIDEO creative — Meta requires a thumbnail. */
  videoThumbnailFile?: File;
  /** Signed/preview URL for the uploaded thumbnail. */
  videoThumbnailPreview?: string;
  /** S3 storage key of the uploaded thumbnail image, sent on launch. */
  videoThumbnailStorageKey?: string;
  /**
   * YouTube URL for a Google VIDEO creative. Google Ads references a
   * YouTube-hosted video instead of accepting bytes, so Google video uses this
   * instead of videoStorageKey/thumbnail. Sent raw; the backend extracts the id.
   */
  youtubeVideoUrl?: string;
  /** Cards for a CAROUSEL creative (2-10 image+headline+link tiles). */
  carouselCards?: CarouselCardInput[];
}

export interface CarouselCardInput {
  /** S3 storage key of the uploaded card image (sent on launch). */
  imageStorageKey?: string;
  /** Signed/preview URL for the card image. */
  imageUrl?: string;
  headline?: string;
  description?: string;
  /** Per-card click destination; falls back to the creative destinationUrl. */
  destinationUrl?: string;
}

export interface GeneratedCreativeImage {
  imageUrl: string;
  imageStorageKey: string;
  /** AI-generation metadata — absent for manually uploaded images. */
  prompt?: string;
  mimeType: string;
  /** AI-generation metadata — absent for manually uploaded images. */
  model?: string;
  expiresAt: string;
  /**
   * Fingerprint of the picked File this upload came from (absent for
   * AI-generated images). Persisted on the entry — not in component state —
   * so removing the file from the uploader still drops its uploaded image
   * after step navigation or draft rehydration.
   */
  sourceFileFingerprint?: string;
}

export type CallToAction =
  | 'BUY_TICKETS'
  | 'LEARN_MORE'
  | 'SIGN_UP'
  | 'REGISTER'
  | 'GET_TICKETS'
  | 'BOOK_NOW'
  | 'CONTACT_US'
  | 'APPLY_NOW';

export type WizardStep = 'objective-budget' | 'audience-targeting' | 'creative' | 'review-launch';

export const WIZARD_STEPS: WizardStep[] = [
  'objective-budget',
  'audience-targeting',
  'creative',
  'review-launch',
];

export const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  'objective-budget': 'Basic Details',
  'audience-targeting': 'Audience',
  creative: 'Creative',
  'review-launch': 'Review & Launch',
};

export const CTA_OPTIONS: { value: CallToAction; label: string }[] = [
  { value: 'BUY_TICKETS', label: 'Buy Tickets' },
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'REGISTER', label: 'Register' },
  { value: 'GET_TICKETS', label: 'Get Tickets' },
  { value: 'BOOK_NOW', label: 'Book Now' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
];

export const CREATIVE_TYPE_OPTIONS: { value: CreativeType; label: string; description: string }[] =
  [
    { value: CreativeType.IMAGE, label: 'Single Image', description: 'One static image ad' },
    { value: CreativeType.CAROUSEL, label: 'Carousel', description: 'Multiple scrollable images' },
    { value: CreativeType.VIDEO, label: 'Video', description: 'Video ad creative' },
    {
      value: CreativeType.TEXT,
      label: 'Responsive',
      description: 'Platform-optimized responsive ad',
    },
  ];

export const OBJECTIVE_OPTIONS: {
  value: CampaignObjective;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: CampaignObjective.AWARENESS,
    label: 'Awareness',
    description: 'Reach people likely to remember your event',
    icon: 'eye',
  },
  {
    value: CampaignObjective.TRAFFIC,
    label: 'Traffic',
    description: 'Send more people to your event page',
    icon: 'mouse-pointer-click',
  },
  {
    value: CampaignObjective.CONVERSIONS,
    label: 'Conversions',
    description: 'Drive specific actions on your website',
    icon: 'target',
  },
  {
    value: CampaignObjective.TICKET_SALES,
    label: 'Ticket Sales',
    description: 'Maximize ticket purchases for your event',
    icon: 'ticket',
  },
];

/** Platform minimum daily budget in cents */
export const PLATFORM_MIN_DAILY_BUDGET: Record<AdPlatform, number> = {
  [AdPlatform.META]: 100,
  [AdPlatform.GOOGLE]: 500,
  [AdPlatform.TWITTER]: 100,
  // M1 connect-only; M2 will confirm the real TikTok minimum.
  [AdPlatform.TIKTOK]: 100,
};

export interface EventOption {
  id: string;
  name: string;
  startDate: string;
  endDate?: string;
  venue?: string;
  imageUrl?: string;
}

export interface AudienceEstimate {
  /** Headline figure — the midpoint of the bounds below. */
  estimatedReach: number;
  reachLowerBound: number;
  reachUpperBound: number;
  estimatedImpressions: number;
  estimatedCostPerResult: number;
  /**
   * 'META' when the numbers came from Meta's delivery estimate for a real ad
   * account; 'GOOGLE' when they came from Google's reach forecast for a real
   * connected account. There is no locally-approximated source — the request
   * fails instead when neither platform can produce a real estimate.
   */
  source: 'META' | 'GOOGLE';
}

/** Request payload for `POST /targeting/audience-estimate`. Mirrors the
 * backend's `AudienceEstimateDto` — locations/interests carry their full
 * `type` so Meta/Google can be targeted precisely instead of by bare id. */
export interface AudienceEstimateRequest {
  platform?: AdPlatform;
  locations?: LocationTarget[];
  ageMin?: number;
  ageMax?: number;
  googleAgeRange?: GoogleAgeRange;
  genders?: Gender[];
  interests?: InterestTarget[];
}
